import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { LinkedAvatar } from "../components/LinkedProfile";
import { 
  Bell, Check, CheckCheck, Users, Lightbulb, 
  MessageSquare, UserCheck, UserX, MessageCircle, Send
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

const NotificationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const replyInputRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications`, { credentials: 'include' });
      if (response.ok) {
        setNotifications(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);


  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to update notifications');
    }
  };

  const acceptConnection = async (notif) => {
    setActionLoading(notif.notification_id);
    try {
      const response = await fetch(`${API}/network/${notif.related_id}/accept`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => 
          n.notification_id === notif.notification_id 
            ? { ...n, read: true, type: 'connection_accepted', title: 'Connection Accepted', message: n.message.replace('wants to connect', 'is now connected') }
            : n
        ));
        await markAsRead(notif.notification_id);
        toast.success('Connection accepted!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to accept');
      }
    } catch {
      toast.error('Failed to accept connection');
    } finally {
      setActionLoading(null);
    }
  };

  const declineConnection = async (notif) => {
    setActionLoading(notif.notification_id);
    try {
      const response = await fetch(`${API}/network/${notif.related_id}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => 
          n.notification_id === notif.notification_id 
            ? { ...n, read: true, type: 'connection_declined', title: 'Connection Declined', message: n.message.replace('wants to connect with you', 'connection was declined') }
            : n
        ));
        await markAsRead(notif.notification_id);
        toast.success('Connection declined');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to decline');
      }
    } catch {
      toast.error('Failed to decline connection');
    } finally {
      setActionLoading(null);
    }
  };

  const sendReply = async (e, notif) => {
    e.preventDefault();
    e.stopPropagation();
    if (!replyText.trim() || sendingReply) return;

    setSendingReply(true);
    try {
      const res = await fetch(`${API}/insights/${notif.related_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: replyText.trim() })
      });
      if (res.ok) {
        toast.success('Reply sent!');
        setReplyingTo(null);
        setReplyText("");
        if (!notif.read) markAsRead(notif.notification_id);
      } else {
        toast.error('Failed to send reply');
      }
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      insight_request: Lightbulb,
      insight_response: MessageSquare,
      insight_message: MessageCircle,
      connection_request: Users,
      connection_accepted: UserCheck,
      connection_declined: UserX
    };
    return icons[type] || Bell;
  };

  const getNotificationColor = (type) => {
    const colors = {
      insight_request: 'bg-violet-100 text-violet-600',
      insight_response: 'bg-green-100 text-green-600',
      insight_message: 'bg-blue-100 text-blue-600',
      connection_request: 'bg-purple-100 text-purple-600',
      connection_accepted: 'bg-emerald-100 text-emerald-600',
      connection_declined: 'bg-slate-100 text-slate-500'
    };
    return colors[type] || 'bg-slate-100 text-slate-600';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getRoute = (notif) => {
    switch (notif.type) {
      case 'insight_request':
        return `/insights/give/${notif.related_id}`;
      case 'insight_response':
        const toolType = notif.metadata?.tool_type;
        if (toolType === 'swot') return '/tools/swot';
        if (toolType === 'core_values') return '/tools/values';
        return '/dashboard';
      case 'insight_message':
        return `/insights/give/${notif.related_id}`;
      case 'connection_accepted':
      case 'connection_declined':
        return '/network';
      default:
        return null;
    }
  };

  const handleNotificationClick = async (notif) => {
    if (notif.type === 'connection_request') return;
    if (!notif.read) await markAsRead(notif.notification_id);
    const route = getRoute(notif);
    if (route) navigate(route);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="notifications-page">
      {/* Page Title */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              Notifications
            </h1>
            {unreadCount > 0 && (
              <p className="text-sm text-slate-500 mt-1">{unreadCount} unread</p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-violet-600 hover:text-violet-700"
              data-testid="mark-all-read-button"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map((notif) => {
              const Icon = getNotificationIcon(notif.type);
              const isMessageType = notif.type === 'insight_message';
              const meta = notif.metadata || {};
              const isReplying = replyingTo === notif.notification_id;
              
              return (
                <div 
                  key={notif.notification_id}
                  className={`card overflow-hidden transition-all ${
                    notif.read ? 'bg-white' : 'bg-violet-50 border-violet-100'
                  } ${notif.type !== 'connection_request' ? 'cursor-pointer hover:shadow-md' : ''}`}
                  data-testid={`notification-${notif.notification_id}`}
                >
                  <div
                    className="p-4 flex items-start gap-4"
                    onClick={() => handleNotificationClick(notif)}
                  >
                    {/* Avatar when sender info is available, icon for others */}
                    {(meta.sender_picture || meta.sender_name) ? (
                      <LinkedAvatar username={notif.from_user_username} className="flex-shrink-0">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={meta.sender_picture} alt={meta.sender_name} />
                          <AvatarFallback className={`text-sm ${getNotificationColor(notif.type)}`}>
                            {meta.sender_name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                      </LinkedAvatar>
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationColor(notif.type)}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        {isMessageType ? (
                          <div className="flex items-center gap-2">
                            <h3 className={`font-semibold ${notif.read ? 'text-slate-700' : 'text-slate-900'}`}>
                              {notif.title}
                            </h3>
                            <span className="text-xs text-slate-400">{meta.subtitle}</span>
                          </div>
                        ) : (
                          <h3 className={`font-medium ${notif.read ? 'text-slate-700' : 'text-slate-900'}`}>
                            {notif.title}
                          </h3>
                        )}
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {formatDate(notif.created_at)}
                        </span>
                      </div>

                      {/* Message content — shown directly for discussion notifications */}
                      <p className={`text-sm mt-1 ${
                        isMessageType
                          ? (notif.read ? 'text-slate-700' : 'text-slate-800')
                          : (notif.read ? 'text-slate-500' : 'text-slate-600')
                      }`} data-testid={isMessageType ? `notif-message-content-${notif.notification_id}` : undefined}>
                        {notif.message}
                      </p>

                      {/* Reply button for discussion messages */}
                      {isMessageType && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setReplyingTo(isReplying ? null : notif.notification_id);
                            setReplyText("");
                          }}
                          className={`mt-2 text-xs font-medium flex items-center gap-1 transition-colors ${
                            isReplying ? 'text-violet-700' : 'text-violet-500 hover:text-violet-700'
                          }`}
                          data-testid={`notif-reply-toggle-${notif.notification_id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          {isReplying ? 'Cancel' : 'Reply'}
                        </button>
                      )}
                      
                      {/* Accept/Decline buttons for pending connection requests */}
                      {notif.type === 'connection_request' && !notif.read && (
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); acceptConnection(notif); }}
                            disabled={actionLoading === notif.notification_id}
                            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-4"
                            data-testid={`accept-connection-${notif.notification_id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            {actionLoading === notif.notification_id ? 'Processing...' : 'Accept'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); declineConnection(notif); }}
                            disabled={actionLoading === notif.notification_id}
                            className="rounded-full px-4"
                            data-testid={`decline-connection-${notif.notification_id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      )}
                    </div>

                    {!notif.read && notif.type !== 'connection_request' && (
                      <div className="w-2.5 h-2.5 bg-violet-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>

                  {/* Inline reply input for discussion messages */}
                  {isReplying && (
                    <form
                      onSubmit={(e) => sendReply(e, notif)}
                      className="px-4 pb-4 flex items-center gap-2 border-t border-slate-100 pt-3"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`notif-reply-form-${notif.notification_id}`}
                    >
                      <input
                        ref={replyInputRef}
                        type="text"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={`Reply to ${meta.sender_name || 'them'}...`}
                        className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-full px-4 py-2 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-200"
                        data-testid={`notif-reply-input-${notif.notification_id}`}
                      />
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!replyText.trim() || sendingReply}
                        className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-9 w-9 p-0 flex-shrink-0"
                        data-testid={`notif-reply-send-${notif.notification_id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-slate-400" />
            </div>
            <h3 
              className="text-xl font-semibold text-slate-900 mb-2"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              No Notifications Yet
            </h3>
            <p className="text-slate-600 max-w-sm mx-auto">
              When you receive insight requests, connection invitations, or responses from your network, 
              they'll appear here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
